import React, {useState} from "react";
import {Grid, Typography} from "@mui/material";
import BookingPaymentBeforeYouBook from "./BookingPaymentBeforeYouBook";
import RequestBookSpaceButton from "./RequestBookSpaceButton";
import SimpleConfirmationDialog from "../../SimpleConfirmationDialog";
import {sendPurchaseEvent} from "../../../util/GoogleTagManagerUtil";
import {bookSpace} from "../../../views/requests/booking-requests";
import Busy from "../../Busy";
import {withSnackbar, WithSnackbarProps} from "../../hocs/withSnackbar";
import {getErrorMessageForStandardResponse} from "../../../util/NetworkErrorUtil";
import { useNavigate } from "react-router";
import { getLocationAllowsDailyBookings } from "../../../util/AccountUtils";
import {scrollToTop} from "../../../util/BookingUtil";
import type {Supplier} from "../../../types/Supplier";

interface RequestSpaceProps extends WithSnackbarProps {
  rentalAgreementURL: string;
  hasAcceptedRentalAgreement: boolean;
  setHasAcceptedRentalAgreement: (value: boolean) => void;
  requestBookSpaceObject: Record<string, any>;
  bookingLocation: Supplier;
  setBookingNumber: (value: string) => void;
  setShowConfirmation: (value: boolean) => void;
  instantApproval: boolean;
  handleDisableBookingChanges: () => void;
  disableBookingChanges: boolean;
  isBrokeredBooking: boolean;
}

const confirmationBodyPaddingSx = {
  paddingBottom: '0.86rem',
} as const;

const RequestSpace = (props: RequestSpaceProps) => {
  const [showConfirmBookingDialog, setShowConfirmBookingDialog] = useState(false);
  const closeBookingConfirmedDialog = () => setShowConfirmBookingDialog(false);
  const router = useNavigate();

  const {
    rentalAgreementURL,
    hasAcceptedRentalAgreement,
    setHasAcceptedRentalAgreement,
    requestBookSpaceObject,
    bookingLocation,
    setBookingNumber,
    setShowConfirmation,
    instantApproval,
    handleDisableBookingChanges,
    disableBookingChanges,
    isBrokeredBooking,
    snackbarShowMessage,
  } = props;

  const completeBooking = () => {
    handleDisableBookingChanges();
    Busy.set(true);
    bookSpace(requestBookSpaceObject)
      .then((res) => {
        const {body} = res;
        if (body.orderNumber) {
          setBookingNumber(body.orderNumber)
        }
        router(`/booking-confirmation/${body?.id}`)
        setShowConfirmation(true)
        scrollToTop();
        sendPurchaseEvent(body)
      })
      .catch((err: unknown) => {
        handleDisableBookingChanges();
        const error = getErrorMessageForStandardResponse(err, "There was an error completing your booking. Please try again.");
        const displayError = error?.includes("Invalid amount")
          ? "There was an issue with your payment method. Please contact support"
          : error;
        snackbarShowMessage(displayError, "error", 15000);
      })
      .finally(() => {
        Busy.set(false);
        closeBookingConfirmedDialog();
      });
  };

  return <Grid item>
    {!disableBookingChanges &&
      <>
        <BookingPaymentBeforeYouBook
          rentalAgreementURL={rentalAgreementURL}
          hasAcceptedRentalAgreement={hasAcceptedRentalAgreement}
          setHasAcceptedRentalAgreement={setHasAcceptedRentalAgreement}
          instantApproval={instantApproval}
          isBrokeredBooking={isBrokeredBooking}
        />

        <RequestBookSpaceButton
          requestBookSpaceObject={requestBookSpaceObject}
          allowDaily={getLocationAllowsDailyBookings(bookingLocation?.pricePerDay)}
          hasAcceptedRentalAgreement={hasAcceptedRentalAgreement}
          disableBookingChanges={disableBookingChanges}
          isBrokeredBooking={isBrokeredBooking}
          setShowConfirmBookingDialog={setShowConfirmBookingDialog}
          instantApproval={instantApproval}
          bookSpace={completeBooking}
        />
      </>
    }
    <SimpleConfirmationDialog
      open={showConfirmBookingDialog}
      onClose={closeBookingConfirmedDialog}
      onConfirm={completeBooking}
      title={"Request Booking"}
      body={<Typography sx={confirmationBodyPaddingSx} variant={'body2'} color={'textPrimary'}>Your
        reservations won&#39;t be confirmed until the request is accepted. You will be notified by email and in your &quot;My
        Bookings&quot; page automatically whether your request is accepted within 30 minutes of submitting.</Typography>}
    />
  </Grid>
}

export default withSnackbar(RequestSpace);
